// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import ProductBadges from "@guestsupply/App/Components/Common/ProductBadges";
import setAutoDeliveryModalClosed from "@guestsupply/App/Handlers/AutoDelivery/SetAutoDeliveryModalClosed";
import xUseWidgetBadges from "@guestsupply/App/Hooks/xUseWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { VariantDisplayTypeValues } from "@insite/client-framework/Types/ApiModels";
import ProductAttributes, { ProductAttributesStyles } from "@insite/content-library/Components/ProductAttributes";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import ProductListVariantSwatch from "@insite/content-library/Widgets/ProductList/ProductListVariantSwatch";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showAvailability: boolean;
    showAttributes: boolean;
    showImageBadges?: boolean;
    maxImageBadges?: number;
    showTextBadges?: boolean;
    maxTextBadges?: number;
    showPackDescription?: boolean;
    showShippingWeight?: boolean;
    // XNG Changes
    extendedStyles?: ProductListProductInformationStyles;
    isGridView?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
});

const mapDispatchToProps = {
    setAutoDeliveryModalClosed,
};

type Props = ReturnType<typeof mapStateToProps> &
    OwnProps &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

export interface ProductListProductInformationStyles {
    container?: GridContainerProps;
    brandStyles?: ProductBrandStyles;
    descriptionStyles?: ProductDescriptionStyles;
    brandGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    partNumbersGridItem?: GridItemProps;
    swatchGridItem?: GridItemProps;
    imageBadgeGridItem?: GridItemProps;
    textBadgeGridItem?: GridItemProps;
    partNumbersStyles?: ProductPartNumbersStyles;
    availabilityGridItem?: GridItemProps;
    availabilityStyles?: ProductAvailabilityStyles;
    attributesGridItem?: GridItemProps;
    attributes?: ProductAttributesStyles;
    searchScoreDataGridItem?: GridItemProps;
    // XNG-CHANGES
    smallHeadingStyle?: SmallHeadingAndTextStyles;
    surchargesGridItem?: GridItemProps;
    surchargesTooltip?: TooltipPresentationProps;
    totalFreightChargesLink?: LinkPresentationProps;
}

export const productInformationStyles: ProductListProductInformationStyles = {};

const ProductListProductInformation = ({
    productContext,
    settingsCollection,
    showBrand,
    showTitle,
    showPartNumbers,
    showAvailability,
    showAttributes,
    showImageBadges,
    maxImageBadges,
    showTextBadges,
    maxTextBadges,
    // XNG-CHANGES
    showShippingWeight = true,
    showPackDescription = true,
    extendedStyles,
    setAutoDeliveryModalClosed,
    isGridView = false,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(productInformationStyles, extendedStyles));

    const { product } = productContext;
    if (!product) {
        return null;
    }
    // XNG-CHANGES
    const { ootbBadgesAndTags, customBadgesAndTags } = xUseWidgetBadges({
        productContext,
        showTextBadges,
        showImageBadges,
    });

    let variantDisplayTrait = null;
    if (product.variantTraits && product.variantTraits.length > 0) {
        const swatchedViewList = [
            VariantDisplayTypeValues.SwatchDropdown,
            VariantDisplayTypeValues.SwatchList,
            VariantDisplayTypeValues.SwatchGrid,
        ];
        const sortedVaraintTraits = product.variantTraits.slice().sort((a, b) => a.sortOrder - b.sortOrder);
        variantDisplayTrait = sortedVaraintTraits.find(x => swatchedViewList.includes(x.displayType));
    }
    const handleDescriptionClick = () => {
        setAutoDeliveryModalClosed({});
    };
    return (
        <GridContainer {...styles.container}>
            {product.brand && showBrand && (
                <GridItem {...styles.brandGridItem}>
                    <ProductBrand brand={product.brand} extendedStyles={styles.brandStyles} />
                </GridItem>
            )}
            {showTitle && (
                <GridItem {...styles.descriptionGridItem}>
                    <ProductDescription
                        onClick={handleDescriptionClick}
                        product={productContext}
                        extendedStyles={styles.descriptionStyles}
                    />
                </GridItem>
            )}
            {/* XNG-CHANGES */}
            {showPartNumbers && (
                <GridItem {...styles.partNumbersGridItem}>
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        extendedStyles={styles.partNumbersStyles}
                        showManufacturerItem={false}
                        showCustomerName={false}
                        {...{ customerPartNumber: product?.properties?.customerPartNumber }}
                    />
                </GridItem>
            )}
            {/* XNG-CHANGES */}
            {showPackDescription && product.packDescription && (
                <GridItem {...styles.partNumbersGridItem}>
                    <SmallHeadingAndText
                        heading={translate("Case Pack")}
                        text={product.packDescription}
                        extendedStyles={styles.smallHeadingStyle}
                    />
                </GridItem>
            )}
            {variantDisplayTrait && (
                <GridItem {...styles.swatchGridItem}>
                    <ProductListVariantSwatch product={productContext} variantTrait={variantDisplayTrait} />
                </GridItem>
            )}
            {/* xng-change */}
            {/* {(showImageBadges || showTextBadges) && (
                <GridItem {...styles.textBadgeGridItem} {...{ isGridView }}>
                    {showImageBadges && imageBadges && (
                        <ProductImageBadges badgeList={imageBadges} maxBadges={maxImageBadges ?? 4} />
                    )}

                    {showTextBadges && textBadges && (
                        <ProductTextBadges badgeList={textBadges} maxBadges={maxTextBadges ?? 4} />
                    )}
                </GridItem>
            )} */}
            <ProductBadges ootbBadgesAndTags={ootbBadgesAndTags} customBadgesAndTags={customBadgesAndTags} />

            {showAttributes && (
                <GridItem {...styles.attributesGridItem}>
                    <ProductAttributes maximumNumberAttributeTypes={3} extendedStyles={styles.attributes} />
                </GridItem>
            )}
            {product.properties?.hasActiveFreights && product.properties?.hasActiveFreights.toLowerCase() === "true" && (
                <GridItem {...styles.surchargesGridItem}>
                    <Tooltip
                        {...styles.surchargesTooltip}
                        text={siteMessage("GUEST_Surcharges_Icon_Hover", "This product has surcharges") as string}
                        triggerComponent={
                            <Link
                                {...styles.totalFreightChargesLink}
                                data-test-selector="totalFreightCharges"
                                aria-label="total Freight Charges"
                            ></Link>
                        }
                    />
                </GridItem>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductListProductInformation));
